import { memo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card } from '@/components/ui/Card'
import { CheckCircle, Clock, FolderOpen, ChevronRight } from 'lucide-react'
import type { JiraPersonSummary } from '@/types/jira'

interface JiraPersonCardProps {
  person: JiraPersonSummary
}

const JiraPersonCard = memo(function JiraPersonCard({ person }: JiraPersonCardProps) {
  const navigate = useNavigate()
  const { user, openTaskCount, completedTaskCount, topProjects } = person

  const handleClick = () => {
    navigate(`/jira/person/${user.accountId}`)
  }

  const avatarUrl = user.avatarUrls?.['48x48']
  const initials = user.displayName
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)

  return (
    <Card
      variant="hover"
      padding="md"
      onClick={handleClick}
      className="cursor-pointer group"
    >
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0">
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt={user.displayName}
              className="w-12 h-12 rounded-full ring-2 ring-white dark:ring-gray-700 shadow-md"
            />
          ) : (
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center text-white font-semibold shadow-md">
              {initials}
            </div>
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-base font-semibold text-gray-900 dark:text-white truncate">
              {user.displayName}
            </h3>
            <ChevronRight className="h-5 w-5 text-gray-400 group-hover:text-primary-500 transition-colors" />
          </div>

          <div className="grid grid-cols-2 gap-3 mb-3">
            <div className="flex items-center gap-2">
              <div className="p-1.5 bg-amber-100 dark:bg-amber-900/30 rounded-md">
                <Clock className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">Open</p>
                <p className="text-sm font-semibold text-gray-900 dark:text-white">{openTaskCount}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="p-1.5 bg-green-100 dark:bg-green-900/30 rounded-md">
                <CheckCircle className="h-3.5 w-3.5 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">Done</p>
                <p className="text-sm font-semibold text-gray-900 dark:text-white">{completedTaskCount}</p>
              </div>
            </div>
          </div>

          {topProjects.length > 0 && (
            <div className="flex items-center gap-2">
              <FolderOpen className="h-3.5 w-3.5 text-gray-400" />
              <div className="flex flex-wrap gap-1.5">
                {topProjects.slice(0, 3).map(project => (
                  <span
                    key={project.key}
                    className="px-2 py-0.5 text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-md"
                  >
                    {project.key}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </Card>
  )
})

export default JiraPersonCard

